import { callApi } from '@/api/api.config';
import { buildPageSortFilterParams } from '@/api/api.params';
import { PaginatedResponse } from '@/api/api.types';
import { GeoJSONFeatureCollection } from '@/components/maps/map.types';
import { Owner } from '@/modules/Users/Owner.schema';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';

export type Farm = {
  farm_id: string;
  farm_name: string;
  primary_user: Owner | null;
  status: string;
  breadcrumb: {
    client: {
      id: string;
      name: string;
    };
    project: {
      id: string;
      name: string;
    };
    farm: {
      id: string;
      name: string;
    };
  };
  sampling_event?: number | null;
};

export type FarmFields = {
  field_id: string;
  field_num: string;
  customer_field_name: string;
};

export const useFarmList = (pagination: PaginationState, sorting: SortingState, columnFilters: ColumnFiltersState, projectId?: string) => {
  return useQuery({
    queryKey: ['FarmList', projectId, pagination, sorting, columnFilters],
    queryFn: () => fetchFarmListData(pagination, sorting, columnFilters, projectId),
    placeholderData: keepPreviousData,
  });
};

const fetchFarmListData = async (
  pagination: PaginationState,
  sorting: SortingState,
  columnFilters: ColumnFiltersState,
  projectId?: string
): Promise<PaginatedResponse<Farm>> => {
  const url = projectId ? `farm/project-farms/${projectId}/` : `farm/farms/`;
  return callApi({
    method: 'GET',
    url: url,
    params: buildPageSortFilterParams({ pagination, sorting, columnFilters }),
  }).then((response) => response.data);
};

export const useMap = (farmId: string, fieldId?: string) => {
  return useQuery({
    queryKey: ['Map', farmId, fieldId],
    queryFn: () => fetchMapData(farmId, fieldId),
  });
};

const fetchMapData = async (farmId: string, fieldId?: string) => {
  const url = fieldId ? `farm/geojson/field/${fieldId}/` : `/farm/geojson/farm/${farmId}/`;
  return callApi({ method: 'GET', url: url }).then((response) => response.data);
};

export const fetchFarmMap = async (farmId?: string) => {
  if (!farmId) {
    return Promise.reject(new Error('Invalid farm ID'));
  }
  return callApi({ method: 'GET', url: `/farm/geojson/farm/${farmId}/` }).then((response) => response.data as GeoJSONFeatureCollection);
};

export const useFarmMap = (farmId?: string) =>
  useQuery({
    queryKey: ['Farm Map', farmId],
    queryFn: () => fetchFarmMap(farmId),
    enabled: !!farmId,
  });

export const fetchFieldMap = async (field?: string) => {
  if (!field) {
    return Promise.reject(new Error('Invalid field ID'));
  }
  return callApi({ method: 'GET', url: `/farm/geojson/field/${field}/` }).then((response) => response.data as GeoJSONFeatureCollection);
};

export const useFieldMap = (field?: string) =>
  useQuery({
    queryKey: ['Field Map', field],
    queryFn: () => fetchFieldMap(field),
    enabled: !!field,
  });

export const fetchFarmFields = async (farmId: string): Promise<FarmFields[]> => {
  const { data } = await callApi({
    method: 'GET',
    url: `/farm/farm_fields/${farmId}/`,
  });
  return data as FarmFields[];
};

export const useFarmFields = (farmId: string) =>
  useQuery({
    queryKey: ['farmFields', farmId],
    queryFn: () => fetchFarmFields(farmId),
    enabled: !!farmId,
    initialData: [],
    placeholderData: keepPreviousData,
  });
